import { cn } from "@/lib/utils"
import { Card } from "../../ui/card"
import { Button } from "../../ui/button"
import { BodyMedium, H2, P, Small } from "../../ui/typography"
import { PageHeader } from "../OnboardingLayout"
import { ChevronDown, ChevronRight } from "lucide-react"
import React from "react"
import { SidebarMenuSub, SidebarMenuSubItem } from "@/components/ui/sidebar"
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { Path, useNavigate } from "@/router"

const isItemActive = (item: SidebarItem, activeId?: SidebarIds): boolean => {
	if (item.id === activeId) return true
	return item.items.some((subItem) => isItemActive(subItem, activeId))
}

const navSections = [
	{
		title: "Report Settings",
		id: "report-settings",
		isActive: false,
		items: [
			{
				id: "reporting-period",
				title: "Reporting Period",
				path: "/report/:id/edit/period",
				items: [],
			},
			{
				id: "addresses",
				title: "Addresses",
				path: "/report/:id/edit/address",
				items: [],
			},
			{
				id: "work-schedule",
				title: "Work Schedule",
				path: "/report/:id/edit/work-schedule",
				items: [],
			},
			{
				id: "miles-time",
				title: "Miles & Time",
				path: "/report/:id/edit/mileage",
				items: [],
			},
		],
	},
	{
		title: "Report Overview",
		id: "report-overview",
		path: "/report/:id",
		items: [],
	},
] as const satisfies SidebarItem[]

type SidebarIds =
	| (typeof navSections)[number]["id"]
	| (typeof navSections)[number]["items"][number]["id"]

type SettingsLayoutProps = {
	reportId: string
	children: React.ReactNode
	className?: string
	title: string
	description?: string
	activeSection?: SidebarIds
	onSave: () => void
	onChangeSection?: (path: Path) => void
	savingInProcess: boolean
}

export function SettingsLayout(props: SettingsLayoutProps) {
	const navigate = useNavigate()

	const sectionsWithActive = React.useMemo(() => {
		return navSections.map((section) => ({
			...section,
			isActive: isItemActive(section, props.activeSection),
			items: section.items.map((item) => ({
				...item,
				isActive: item.id === props.activeSection,
			})),
		}))
	}, [props.activeSection])

	return (
		<div className="min-h-screen bg-gray-50 px-6 py-8">
			<div className={cn("container mx-auto", props.className)}>
				<PageHeader />
				<div className="max-w-6xl mt-7 flex gap-8">
					<div className="w-[20%]">
						<SettingsSidebar
							items={sectionsWithActive}
							onSelectSection={(item) => {
								if (item?.path) {
									navigate(item.path, {
										params: { id: props.reportId },
									})
								}
							}}
						/>
					</div>
					<Card className="relative p-6 flex flex-col gap-8 w-[60%]">
						<div className="flex flex-col justify-between pb-6 border-b-slate-200 border-b-2">
							<div>
								<div className="flex items-start justify-between">
									<H2>{props.title}</H2>
								</div>
								<P className="text-muted-foreground font-normal">
									{props.description}
								</P>
							</div>
						</div>
						{props.children}
						<div>
							<Button disabled={props.savingInProcess} onClick={props.onSave} size={"lg"}>
								{props.savingInProcess ? "Saving" : "Save & Update Report"}
							</Button>
						</div>
					</Card>
				</div>
			</div>
		</div>
	)
}
type SidebarItem = {
	id: string
	title: string
	path?: Path
	isActive?: boolean
	items: SidebarItem[]
}
type SidebarProps = {
	items: SidebarItem[]
	onSelectSection: (item: SidebarItem) => void
}
const SettingsSidebar: React.FC<SidebarProps> = (props) => {
	return (
		<div>
			{props.items.map((item) => (
				<Collapsible
					key={item.title}
					asChild
					defaultOpen={item.isActive}
					className="group/collapsible"
				>
					<div>
						<CollapsibleTrigger asChild>
							<Button
								size={"sm"}
								variant="ghost"
								className={cn(
									"w-full flex justify-start hover:bg-accent px-4 py-1",
									item.isActive && "bg-slate-100/50"
								)}
								onClick={() => {
									props.onSelectSection(item)
								}}
							>
								<Small className="font-medium text-popover-foreground">
									{item.title}
								</Small>
								{item.items.length > 0 && (
									<ChevronDown className="ml-1 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180" />
								)}
							</Button>
						</CollapsibleTrigger>
						<CollapsibleContent>
							<SidebarMenuSub>
								{item.items?.map((subItem) => (
									<SidebarMenuSubItem key={subItem.title}>
										<Button
											size={"sm"}
											variant="ghost"
											className={cn(
												"w-full justify-start text-muted-foreground hover:bg-accent px-4 py-1",
												subItem.isActive && "bg-accent"
											)}
											onClick={() => {
												props.onSelectSection(subItem)
											}}
										>
											<Small className="font-medium text-popover-foreground">
												{subItem.title}
											</Small>
										</Button>
									</SidebarMenuSubItem>
								))}
							</SidebarMenuSub>
						</CollapsibleContent>
					</div>
				</Collapsible>
			))}
		</div>
	)
}
